import {NetworkProviderOptions} from "@/features/api/lib/network-provider";
import {addBreadcrumb, captureMessage} from "@sentry/react";

import {pageWake} from "./pageWake";

type RequestTarget = {host: string; path: string};

const describeTarget = (url: string): RequestTarget => {
  try {
    const {host, pathname} = new URL(url);
    return {host, path: pathname};
  } catch {
    return {host: "unknown", path: ""};
  }
};

/**
 * RTDB paths name the poll and question, which is what a failed write has to
 * be tied to (`results/<slug>/<uid>/answers/<questionId>.json`; introsets use
 * the same shape under `introset-results`). Tags rather than the title, so
 * every failure of one kind is one countable pattern.
 */
const RESULT_NODES = new Set(["results", "introset-results", "results-logs"]);

const rtdbTags = (path: string): Record<string, string> => {
  const [node, pollSlug, , child, leaf] = path
    .replace(/\.json$/, "")
    .split("/")
    .filter(Boolean);

  if (!node || !RESULT_NODES.has(node)) return {};
  return {
    rtdbNode: node,
    ...(pollSlug && {pollSlug}),
    ...(child === "answers" && leaf && {questionId: leaf}),
  };
};

/**
 * Only the server's own error text: an API error body can echo what was sent,
 * and breadcrumbs go out with every report. The text itself can still name
 * the email on the login endpoints; utils/sentryScrub redacts addresses.
 */
const readResponseError = async (
  response: Response,
): Promise<string | undefined> => {
  let text: string;
  try {
    text = await response.clone().text();
  } catch {
    return "unreadable body";
  }
  // A bare 401/403 has no body; calling it non-JSON would send the reader
  // looking for a proxy page that never existed.
  if (!text) return "empty body";

  let body: {error?: unknown; message?: unknown} | null;
  try {
    body = JSON.parse(text);
  } catch {
    // A proxy's 503 page is HTML; failing here used to lose the report itself.
    return "non-JSON body";
  }
  // Strings only: an object here could be anything the server chose to echo.
  const candidates = [
    body?.error,
    body?.message,
    (body?.error as {message?: unknown} | undefined)?.message,
  ];
  return candidates.find((candidate) => typeof candidate === "string") as
    | string
    | undefined;
};

/**
 * Logs a failed request to Sentry (and through it to Better Stack).
 * The title never carries the address: for RTDB it holds `?auth=<ID token>`,
 * and a per-address title made every failure its own single-hit pattern.
 */
export const logFetchError = async (
  request: Request,
  // Normalised by makeRequest, so the verb is always set. It is the real one:
  // RTDB writes go out as POST with a method override.
  requestOptions: NetworkProviderOptions & {method: string},
  response?: Response,
  error?: Error,
) => {
  const method = requestOptions.method.toUpperCase();
  const {host, path} = describeTarget(response?.url || request.url);
  const tags = {
    "http.method": method,
    "url.host": host,
    ...rtdbTags(path),
  };

  // Size only: the body of an RTDB write is the respondent's answer.
  if (requestOptions.json !== undefined) {
    addBreadcrumb({
      category: "Fetch",
      message: "Fetch request payload",
      level: "info",
      data: {method, size: String(requestOptions.body ?? "").length},
    });
  }

  if (response) {
    addBreadcrumb({
      category: "Fetch",
      message: "Fetch response",
      level: "info",
      data: {
        status: response.status,
        error: await readResponseError(response),
      },
    });

    captureMessage(`Fetch error: HTTP ${response.status} (${method} ${host})`, {
      level: "error",
      tags: {...tags, "http.status_code": String(response.status)},
      extra: {path},
    });
  }

  if (error) {
    // A drop while the page is hidden, offline or just woken says nothing
    // about our servers: phones thaw tabs before their radio is back. Kept as
    // a breadcrumb so a real error right after still shows it.
    // A request the caller aborted (a stalled finish save, #1417) says nothing
    // about the servers either.
    const reason =
      error.name === "AbortError" ? "aborted" : pageWake.unreliableReason();
    if (reason) {
      addBreadcrumb({
        category: "Fetch",
        message: error.message,
        level: "warning",
        data: {...tags, reason},
      });
      return;
    }

    captureMessage(error.message, {level: "error", tags, extra: {path}});
  }
};
